import React from "react";

import {
  Padding,
  ReportTitle,
  Sheet,
  Text,
  Layout,
  AnchorLink,
  Margin,
} from "@kidslivesafe/sc-react";
import { useAppSelector, useHandlePageNavigate } from "../../../../hooks/hooks";
import { selectBrandInfo } from "../../../../features/brand/brandSlice";
import { selectPrimarySubscription } from "../../../../features/subscriptions/subscriptionSlice";
import { selectMemberData } from "../../../../features/authentication/authenticationSlice";

import "./cancel-membership.css";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";
import {
  capFirstLetter,
  formatPhoneNumber,
  parseDate,
} from "../../../../utils";

const CancelMembership: React.FC = () => {
  const pageNavigate = useHandlePageNavigate();

  const brandInfo = useAppSelector(selectBrandInfo);
  const memberData = useAppSelector(selectMemberData);
  const subscriptionDetails = useAppSelector(selectPrimarySubscription);
  const currentBreakpoint = useCurrentBreakpoint();

  return (
    <>
      <ReportTitle reportTitle="How to Cancel" />
      <Sheet>
        <Padding
          top={currentBreakpoint === "desktop" ? "xl" : "md"}
          left="md"
          right="md"
          bottom="md"
        >
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "md",
            }}
          >
            <Layout type="flex" flexProps={{ gap: "sm", direction: "column" }}>
              <Margin bottom="md">
                <Text tag="h2">
                  How to Cancel Your {brandInfo.name} Membership
                </Text>
              </Margin>

              <Margin bottom="md">
                <Text fontWeight="bold">Need Help with Your Account?</Text>
              </Margin>

              <Text>
                Our friendly support team is available 7 days a week to answer
                questions or help you manage your membership.
              </Text>
              <Text>
                To cancel, please call at{" "}
                <AnchorLink
                  inline
                  color="base"
                  fontWeight="bold"
                  phoneNumber
                  hoverUnderline
                  href={brandInfo.phoneNumber}
                >
                  {formatPhoneNumber(brandInfo.phoneNumber)}
                </AnchorLink>{" "}
                or{" "}
                <AnchorLink
                  fontWeight="bold"
                  href={`${brandInfo.homeUrl}/help-center/contact`}
                  hoverUnderline
                  inline
                >
                  click here
                </AnchorLink>{" "}
                for support.
              </Text>
              <Text>
                You can also cancel your account online,{" "}
                <AnchorLink
                  fontWeight="bold"
                  role="button"
                  handleClick={() => {
                    pageNavigate("/cancel-feedback");
                  }}
                  hoverUnderline
                  inline
                >
                  click here
                </AnchorLink>
                .
              </Text>
              <Text>
                Canceling your account will end your access to all reports and
                monitoring at the end of your current billing period. You can
                reactivate at any time by logging in or contacting our support
                team.
              </Text>
            </Layout>
            <Layout type="flex" flexProps={{ gap: "sm", direction: "column" }}>
              <Margin top="md">
                <Text tag="h3">Why Stay with {brandInfo.name}?</Text>
              </Margin>

              <Layout
                type="flex"
                flexProps={{ gap: "md", direction: "column" }}
              >
                <Text>
                  {brandInfo.name} was founded with the mission to make it safe
                  and easy for people to find and learn the truth about each
                  other. Don’t miss out on comprehensive searches, real-time
                  alerts, and access to the best-in-class people and property
                  reports.
                </Text>
              </Layout>
            </Layout>
            <Layout type="flex" flexProps={{ gap: "sm", direction: "column" }}>
              <Margin top="md">
                <Text tag="h3">Your Account Includes</Text>
              </Margin>
              <ul>
                <li>Name searches</li>
                <li>Comprehensive people reports</li>
                <li>
                  Address, contact info, arrest and warrant checks, financial
                  issues, and more
                </li>
                <li>Active monitoring with email alerts when info changes</li>
                <li>Social media information</li>
                <li>
                  Property search & reports (160+ million property records)
                </li>
                <li>Reverse phone lookups</li>
              </ul>
            </Layout>
            <Layout type="flex" flexProps={{ gap: "sm", direction: "column" }}>
              <Margin top="md">
                <Text tag="h3">Your Account Includes</Text>
              </Margin>
              <ul>
                <li>
                  Status:{" "}
                  {subscriptionDetails?.cancelDate
                    ? "Canceled"
                    : capFirstLetter(
                        memberData.subscriptionStatus
                          ? memberData.subscriptionStatus
                          : "",
                      )}{" "}
                </li>
                <li>
                  {" "}
                  Your access is active until:{" "}
                  {memberData.accessExpirationDate
                    ? parseDate(memberData.accessExpirationDate, "MM/DD/YYYY")
                    : "N/A"}
                </li>
                <li>Plan: {brandInfo.name}</li>
                <li>
                  Price: ${subscriptionDetails?.price} every{" "}
                  {subscriptionDetails?.billingInterval} days
                </li>
              </ul>
            </Layout>
            <Layout type="flex" flexProps={{ gap: "sm", direction: "column" }}>
              <Margin top="md">
                <Text tag="h3">Switch Plans</Text>
              </Margin>
              <Layout
                type="flex"
                flexProps={{ gap: "md", direction: "column" }}
              >
                <Text>
                  If you prefer, you can{" "}
                  <AnchorLink
                    inline
                    fontWeight="bold"
                    hoverUnderline
                    href={`${brandInfo.homeUrl}/help-center/contact`}
                  >
                    switch plans here
                  </AnchorLink>
                  .
                </Text>
              </Layout>
            </Layout>
            <div className="page-cancel-membership_divider" />
            <Layout type="flex" flexProps={{ gap: "sm", direction: "column" }}>
              <Text tag="h3">Unsubscribe from Email Alerts</Text>
              <Text>
                To unsubscribe from marketing emails,{" "}
                <AnchorLink
                  inline
                  fontWeight="bold"
                  href={brandInfo.unsubscribeUrl}
                  hoverUnderline
                >
                  click here
                </AnchorLink>
                .
              </Text>
              <Text>
                Return to{" "}
                <AnchorLink
                  inline
                  fontWeight="bold"
                  role="button"
                  handleClick={() => {
                    pageNavigate("/my-account");
                  }}
                  hoverUnderline
                >
                  My Account
                </AnchorLink>
              </Text>
            </Layout>
          </Layout>
        </Padding>
      </Sheet>
    </>
  );
};

export default CancelMembership;
